import { Editor } from "@monaco-editor/react";
import {
  AddOutlined,
  ArrowBackOutlined,
  Article,
  AssessmentOutlined,
  Code,
  DataObject,
  Functions,
  HelpOutline,
  InputOutlined,
  PlayArrowOutlined,
  Terminal,
} from "@mui/icons-material";
import {
  Box,
  Button,
  Divider,
  IconButton,
  Paper,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import * as JSON5 from "json5";
import { useEffect, useMemo, useState, useRef } from "react";
import TextFieldsIcon from "@mui/icons-material/TextFields";
import { useNavigate } from "react-router-dom";
import { useStyle } from "./styles";
import { notifyOnPage } from "../../../utils/notifyOnPage";
import api from "../../../services/api";
import { PdfPreview } from "../../../componentes/easyReport/pdfPreview/pdfPreview";
import { STYLES } from "./stylesModel";
import { evalCode } from "../../../componentes/easyReport/pdfPreview/utils/evalCode";
import { VariableForm } from "../../../componentes/easyReport/newVariavel";
import { CUSTOM_ITEMS } from "../../../componentes/easyReport/constants/const";
import ReactMarkdown from "react-markdown";
import { HelpComponent } from "./HelpText";

interface Application {
  name: string;
}

interface Funcoes {
  id: string;
  titulo: string;
}

export interface Variaveis {
  title: string;
  variableName: string;
  applicationId: string[];
  keyName: any[];
  multi: boolean;
}

interface NewFuncao {
  nome: string;
  description: string;
  variavel: Variaveis[];
  code: string;
  view: string;
  style: string;
}

function NewFunction() {
  const theme = useTheme();
  const styles = useStyle(theme);
  const navigate = useNavigate();
  const [newFuncao, setNewFuncao] = useState<NewFuncao>({
    nome: "",
    description: "",
    variavel: [
      {
        title: "",
        variableName: "",
        applicationId: [""],
        keyName: [],
        multi: false,
      },
    ],
    code: "",
    view: "",
    style: JSON.stringify(STYLES, null, 2),
  });
  const [allFuncoes, setAllFuncoes] = useState<Funcoes[]>([]);
  const [allApplications, setApplications] = useState<Application[]>([]);
  const [itemsOptions, setItemsOptions] = useState<
    { name: string; key_: string; applicationName: string }[][]
  >([]);
  const [itemsLoading, setItemsLoading] = useState(false);
  const [tabsValue, setTabsValue] = useState(0);
  const [logsContent, setLogsContent] = useState<any>("");
  const [pdfError, setPdfError] = useState<boolean>(false);

  const logsEditorRef = useRef<any>(null);
  const monacoRef = useRef<any>(null);
  const codeEditorRef = useRef<any>(null);
  const codeDebounceRef = useRef<NodeJS.Timeout | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  // Add debounced code state
  const [debouncedCode, setDebouncedCode] = useState(newFuncao.code);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [functions, res] = await Promise.all([
          api.get("/easyReport/v2/findManyFunctions"),
          api.get("easyReport/v2/findManyApplications"),
        ]);
        setAllFuncoes(functions.data);
        setApplications(res.data as Application[]);
      } catch (error: any) {
        notifyOnPage(
          "easyReportV2/perfilRelatorios/novaFuncao",
          error?.response?.data?.error || "Erro carregar aplicação",
          "error",
          3000,
        );
      }
    };
    fetchData();
  }, []);

  const allApplicationsMemo = useMemo(() => allApplications, [allApplications]);

  function useDebouncedValue<T>(value: T, delay: number): T {
    const [debouncedValue, setDebouncedValue] = useState(value);
    const timeoutRef = useRef<NodeJS.Timeout | null>(null);

    useEffect(() => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(() => {
        setDebouncedValue(value);
      }, delay);
      return () => {
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
      };
    }, [value, delay]);

    return debouncedValue;
  }

  const debouncedView = useDebouncedValue(newFuncao.view, 1000);
  const debouncedStyle = useDebouncedValue(newFuncao.style, 1000);

  const rawContentMemo = useMemo(() => [debouncedView], [debouncedView]);

  // Change codeMemo to use debouncedCode
  const codeMemo = useMemo(() => [debouncedCode], [debouncedCode]);

  const variablesMemo = useMemo(() => {
    return [
      newFuncao.variavel.map((v) => ({
        nome: v.variableName,
        itens: v.keyName.map((item) => item.key),
        multiple: v.multi,
      })),
    ];
  }, [newFuncao.variavel]);

  const stylesMemo = useMemo(() => {
    try {
      const parsed = JSON5.parse(debouncedStyle);
      if (!parsed || typeof parsed !== "object") {
        return [STYLES];
      }
      return [parsed];
    } catch {
      return [STYLES];
    }
  }, [debouncedStyle]);

  function isFieldDuplicated(
    variavel: Variaveis[],
    value: string,
    field: keyof Pick<Variaveis, "variableName" | "title">,
  ): boolean {
    const normalized = value.trim().toLowerCase();
    if (!normalized) return false;
    return (
      variavel.filter(
        (v) => (v[field] || "").trim().toLowerCase() === normalized,
      ).length > 1
    );
  }

  const handleSave = async () => {
    try {
      const response = await api.post(
        "/easyReport/v2/createFunction",
        newFuncao,
      );
      if (response && response.status === 201) {
        notifyOnPage(
          "easyReportV2/perfilRelatorios",
          "Função criada com sucesso",
          "success",
          3000,
        );
        navigate("/easyReportV2/perfilRelatorios");
      }
    } catch (error: any) {
      notifyOnPage(
        "easyReportV2/perfilRelatorios/novaFuncao",
        error?.response?.data?.error || "Erro ao criar função",
        "error",
        3000,
      );
    }
  };

  const handleApplicationChange = async (
    index: number,
    v: Variaveis,
    newValues: Application[],
  ) => {
    const appNames = newValues.map((app) => app.name);
    setNewFuncao((prev) => ({
      ...prev,
      variavel: prev.variavel.map((item, i) =>
        i === index
          ? {
              ...item,
              applicationId: appNames,
              keyName: [],
            }
          : item,
      ),
    }));
    abortControllerRef.current?.abort();
    const controller = new AbortController();
    const signal = controller.signal;

    abortControllerRef.current = controller;
    if (appNames.length > 0) {
      try {
        if (appNames.includes("CUSTOM")) {
          const idx = newFuncao.variavel.findIndex((item) => item === v);
          setItemsOptions((prev) => {
            const updated = [...prev];
            updated[idx] = CUSTOM_ITEMS;
            return updated;
          });
        } else {
          setItemsLoading(true);
          const res = await api.post(
            `/easyReport/v2/findManyItemsName`,
            {
              applicationName: appNames,
            },
            { signal },
          );
          const idx = newFuncao.variavel.findIndex((item) => item === v);
          setItemsOptions((prev) => {
            const updated = [...prev];
            updated[idx] = res.data || [];
            return updated;
          });
        }
      } catch (error: any) {
        if (error.name === "CanceledError" || error.name === "AbortError")
          return;

        notifyOnPage(
          "easyReportV2/perfilRelatorios/novaFuncao",
          error?.response?.data?.error || "Erro ao carregar itens",
          "error",
          3000,
        );
      } finally {
        setItemsLoading(false);
      }
    }
  };

  const handleDelete = (index: number) => {
    setNewFuncao((prev) => ({
      ...prev,
      variavel: prev.variavel.filter((_, i) => i !== index),
    }));
  };

  const isNomeDuplicated = useMemo(
    () =>
      allFuncoes.some(
        (p) =>
          newFuncao.nome.trim().toLowerCase() === p.titulo.trim().toLowerCase(),
      ),
    [allFuncoes, newFuncao],
  );

  const allFieldsValid = useMemo(() => {
    if (
      !newFuncao.nome.trim() ||
      !newFuncao.description.trim() ||
      !newFuncao.code.trim() ||
      !newFuncao.view.trim() ||
      !newFuncao.style.trim()
    ) {
      return false;
    }
    return (
      newFuncao.variavel.every(
        (v) =>
          v.variableName.trim() !== "" &&
          v.applicationId.every((id) => id.trim() !== "") &&
          v.keyName.every((obj) => obj.key && obj.key.trim() !== ""),
      ) &&
      newFuncao.variavel.every(
        (v) =>
          newFuncao.variavel.filter(
            (item) =>
              item.variableName.trim().toLowerCase() ===
              v.variableName.trim().toLowerCase(),
          ).length === 1,
      )
    );
  }, [newFuncao]);

  const handleVariableChange = (
    index: number,
    updatedFields: Partial<Variaveis>,
  ) => {
    setNewFuncao((prev) => ({
      ...prev,
      variavel: prev.variavel.map((item, i) =>
        i === index ? { ...item, ...updatedFields } : item,
      ),
    }));
  };

  const handleClickRun = () => {
    const evaluatedCode = evalCode(
      newFuncao.code,
      variablesMemo[0],
      setLogsContent,
    );
    if (evaluatedCode) {
      setLogsContent(evaluatedCode);
    }
  };

  const debounceRef = useRef<NodeJS.Timeout | null>(null);

  function debounceUpdate(
    updater: (value: string) => void,
    value: string,
    delay = 1000,
  ) {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      updater(value);
    }, delay);
  }

  const handleCodeChange = (value: string | undefined) => {
    if (codeDebounceRef.current) clearTimeout(codeDebounceRef.current);
    codeDebounceRef.current = setTimeout(() => {
      setNewFuncao((prev) => ({
        ...prev,
        code: value ?? "",
      }));
    }, 1000);
  };

  useEffect(() => {
    setDebouncedCode(newFuncao.code);
  }, [newFuncao.code]);

  const viewStyleDebounceRef = useRef<NodeJS.Timeout | null>(null);

  const handleViewStyleChange = (value: string | undefined) => {
    if (viewStyleDebounceRef.current)
      clearTimeout(viewStyleDebounceRef.current);
    viewStyleDebounceRef.current = setTimeout(() => {
      setNewFuncao((prev) => ({
        ...prev,
        ...(tabsValue === 1 ? { view: value ?? "" } : { style: value ?? "" }),
      }));
    }, 1000);
  };

  return (
    <Box sx={styles.container}>
      <Box sx={styles.header}>
        <IconButton
          onClick={() => navigate("/easyReportV2/perfilRelatorios/funcao")}
        >
          <ArrowBackOutlined />
        </IconButton>
        <Typography variant="h6">Criar Função</Typography>
      </Box>

      <Paper sx={styles.paper}>
        <Box sx={styles.rowContainer}>
          <Box
            sx={{ flex: 1, flexDirection: "column", display: "flex", gap: 0.5 }}
          >
            <Box sx={styles.boxTitle}>
              <TextFieldsIcon color="primary" />
              <Typography variant="subtitle2">Nome da função</Typography>
            </Box>

            <TextField
              fullWidth
              size="small"
              variant="outlined"
              slotProps={{
                input: {
                  sx: {
                    color: theme.palette.text.secondary,
                  },
                },
              }}
              value={newFuncao.nome}
              onChange={(e) =>
                setNewFuncao((prev) => ({
                  ...prev,
                  nome: e.target.value,
                }))
              }
              placeholder="Nome"
              error={isNomeDuplicated}
              helperText={
                isNomeDuplicated
                  ? "O nome da função precisa ser unico"
                  : "O nome será usado no relatório para exibir o resultado "
              }
            />
          </Box>
          <Box
            sx={{ flex: 1, flexDirection: "column", display: "flex", gap: 0.5 }}
          >
            <Box sx={styles.boxTitle}>
              <Article color="primary" />
              <Typography variant="subtitle2">Descrição</Typography>
            </Box>

            <TextField
              fullWidth
              size="small"
              variant="outlined"
              slotProps={{
                input: {
                  sx: {
                    color: theme.palette.text.secondary,
                  },
                },
              }}
              value={newFuncao.description}
              onChange={(e) =>
                setNewFuncao((prev) => ({
                  ...prev,
                  description: e.target.value,
                }))
              }
              placeholder="Descrição"
            />
          </Box>
        </Box>
        {/* <Box> */}
        <Box sx={{ display: "flex", flexDirection: "column" }}>
          <Box sx={styles.boxTitle}>
            <Functions color="primary" />
            <Typography>Variáveis</Typography>
          </Box>

          {/* 
          <Typography
            sx={{ mb: 1 }}
            color={theme.palette.text.disabled}
            variant="subtitle1"
          >
            Selecione os items e atibua a uma variável
          </Typography> */}
          {/*   </Box> */}

          {newFuncao.variavel.map((v, index) => (
            <Box sx={{ paddingX: 0.6 }}>
              <VariableForm
                key={index}
                variable={v}
                index={index}
                allVariables={newFuncao.variavel}
                allApplications={allApplicationsMemo}
                itemsOptions={itemsOptions[index] || []}
                itemsLoading={itemsLoading}
                canDelete={newFuncao.variavel.length > 1}
                styles={styles}
                onVariableChange={handleVariableChange}
                onApplicationChange={handleApplicationChange}
                onDelete={handleDelete}
                isFieldDuplicated={isFieldDuplicated}
              />
            </Box>
          ))}

          <Button
            onClick={() => {
              const newVariable = [
                ...newFuncao.variavel,
                {
                  title: "",
                  variableName: "",
                  applicationId: [""],
                  keyName: [],
                  multi: false,
                },
              ];
              setNewFuncao((prev) => ({
                ...prev,
                variavel: newVariable,
              }));
            }}
            startIcon={<AddOutlined />}
            sx={{ width: "fit-content" }}
          >
            Adicionar Função
          </Button>
        </Box>
        <Box>
          <Tabs
            value={tabsValue}
            onChange={(_, value) => setTabsValue(value)}
            sx={styles.tabs}
          >
            <Tab
              label="Código"
              icon={<Code />}
              iconPosition="start"
              value={0}
              sx={{
                height: 36,
                minHeight: 36,
                "& .MuiTab-iconWrapper": { minHeight: 0 },
              }}
            />
            <Tab
              label="View"
              icon={<AssessmentOutlined />}
              iconPosition="start"
              value={1}
              sx={{
                height: 36,
                minHeight: 36,
                "& .MuiTab-iconWrapper": { minHeight: 0 },
              }}
            />
            <Tab
              label="Estilo"
              icon={<DataObject />}
              iconPosition="start"
              value={2}
              sx={{
                height: 36,
                minHeight: 36,
                "& .MuiTab-iconWrapper": { minHeight: 0 },
              }}
            />
            <Tab
              label="Ajuda"
              icon={<HelpOutline />}
              iconPosition="start"
              value={3}
              sx={{
                height: 36,
                minHeight: 36,
                "& .MuiTab-iconWrapper": { minHeight: 0 },
              }}
            />
          </Tabs>
          {tabsValue === 0 && (
            <Box sx={styles.monacoContainer}>
              <Box sx={styles.monacoCard}>
                <Box sx={styles.monacoHeader}>
                  <InputOutlined />
                  Input
                  <Button
                    variant="text"
                    color="success"
                    size="small"
                    sx={styles.headerButton}
                    onClick={handleClickRun}
                  >
                    run
                    <PlayArrowOutlined sx={{ fontSize: 16 }} />
                  </Button>
                </Box>
                <Editor
                  key="monaco-editor-code"
                  width="100%"
                  height="100%"
                  defaultLanguage="javascript"
                  defaultValue={newFuncao.code}
                  onChange={handleCodeChange}
                  onMount={(editor) => {
                    codeEditorRef.current = editor;
                  }}
                  theme={theme.palette.mode === "dark" ? "vs-dark" : "light"}
                  options={{
                    formatOnPaste: true,
                    renderValidationDecorations: "on",
                    minimap: { enabled: true },
                    scrollBeyondLastLine: false,
                    formatOnType: true,
                    wordWrap: "on",
                    folding: true,
                    lineNumbers: "on",
                    automaticLayout: true,
                  }}
                />
              </Box>
              <Box sx={styles.monacoCard}>
                <Box sx={styles.monacoHeader}>
                  <Terminal />
                  Logs
                </Box>
                <Box sx={styles.terminal}>
                  <Editor
                    key="monaco-editor-logs"
                    width="100%"
                    height="100%"
                    defaultLanguage="json"
                    value={
                      logsContent === null || logsContent === undefined
                        ? "message: Função não retornou valor"
                        : typeof logsContent === "string"
                          ? logsContent
                          : JSON.stringify(logsContent, null, 2)
                    }
                    theme={theme.palette.mode === "dark" ? "vs-dark" : "light"}
                    onMount={(editor, monaco) => {
                      logsEditorRef.current = editor;
                      monacoRef.current = monaco;
                    }}
                    options={{
                      formatOnPaste: true,
                      renderValidationDecorations: "on",
                      minimap: { enabled: false },
                      scrollBeyondLastLine: false,
                      formatOnType: true,
                      wordWrap: "on",
                      folding: true,
                      lineNumbers: "on",
                      automaticLayout: true,
                      readOnly: true,
                    }}
                  />
                </Box>
              </Box>
            </Box>
          )}
          <Box
            sx={{
              ...styles.monacoContainer,
              display: tabsValue === 0 ? "none" : "flex",
              position: "relative",
            }}
          >
            <Box
              sx={{
                ...styles.monacoCard,
                display: tabsValue === 3 ? "none" : "flex", // Hide editor on Help tab
              }}
            >
              <Box sx={styles.monacoHeader}>
                <InputOutlined />
                Input
              </Box>
              <Editor
                key={
                  tabsValue === 1 ? "monaco-editor-view" : "monaco-editor-style"
                }
                width="100%"
                height="100%"
                defaultLanguage={tabsValue === 1 ? "javascript" : "json"}
                defaultValue={
                  tabsValue === 1 ? newFuncao.view : newFuncao.style
                }
                onChange={handleViewStyleChange}
                theme={theme.palette.mode === "dark" ? "vs-dark" : "light"}
                options={{
                  formatOnPaste: true,
                  renderValidationDecorations: "on",
                  minimap: { enabled: true },
                  scrollBeyondLastLine: false,
                  formatOnType: true,
                  wordWrap: "on",
                  folding: true,
                  lineNumbers: "on",
                  automaticLayout: true,
                }}
              />
            </Box>
            {/* Help Tab Content */}
            {tabsValue === 3 && (
              <Box
                sx={{
                  width: "100%",
                  height: "100%",
                  p: 0,
                  background: theme.palette.background.paper,
                  borderRadius: 2,
                  display: "flex",
                  alignItems: "flex-start",
                  justifyContent: "flex-start",
                }}
              >
                <Box
                  sx={styles.monacoCard}
                  style={{
                    width: "100%",
                    height: "100%",
                    backgroundColor: theme.palette.background.paper,
                  }}
                >
                  <Box sx={styles.monacoHeader}>
                    <HelpOutline />
                    Help / Documentação
                  </Box>
                  <Box
                    sx={{ width: "100%", height: "100%", overflowY: "auto" }}
                  >
                    <HelpComponent />
                  </Box>
                </Box>
              </Box>
            )}
            {/* PDF Preview: always mounted, only hidden on Help tab */}
            <Box
              sx={{
                ...styles.previewContainer,
                display:
                  tabsValue === 0 ? "none" : tabsValue === 3 ? "none" : "flex",
                borderRadius: 2,
                position: "absolute",
                right: 0,
                top: 0,
                height: "100%",
                width: "50%",
                pointerEvents: tabsValue === 3 ? "none" : "auto",
                opacity: tabsValue === 3 ? 0 : 1,
                transition: "opacity 0.2s",
              }}
            >
              <PdfPreview
                variableNames={variablesMemo}
                code={codeMemo}
                rawContent={rawContentMemo}
                styles={stylesMemo}
                setPdfError={setPdfError}
              />
            </Box>
          </Box>
        </Box>
        <Box sx={styles.buttonContainer}>
          <Button
            sx={styles.cancelButton}
            variant="contained"
            onClick={() => navigate("/easyReportV2/perfilRelatorios/funcao")}
          >
            Cancelar
          </Button>
          <Tooltip
            slotProps={{
              tooltip: {
                sx: {
                  maxWidth: 200,
                  flexWrap: "wrap",
                  textAlign: "center",
                },
              },
            }}
            title={
              pdfError
                ? "Erro no codigo de geração do PDF"
                : "Necessario preencher todos os campos"
            }
          >
            <span>
              <Button
                onClick={handleSave}
                disabled={!allFieldsValid || isNomeDuplicated || pdfError}
                variant="contained"
              >
                Salvar
              </Button>
            </span>
          </Tooltip>
        </Box>
      </Paper>
    </Box>
  );
}

export default NewFunction;
